import type { SimpleFieldConfig } from "../types";

// ============================================================================
// COLUMN MANAGEMENT FUNCTIONS
// ============================================================================

/**
 * Handles column resizing with min/max constraints
 */
export const handleColumnResize = (
  columnId: string,
  newWidth: number,
  fields: Record<string, SimpleFieldConfig>,
  setColumnWidths: (widths: (prev: Record<string, number>) => Record<string, number>) => void
): void => {
  const fieldConfig = fields[columnId];
  const minWidth = fieldConfig?.minWidth || 50;
  const maxWidth = fieldConfig?.maxWidth || 500;
  const clampedWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));

  setColumnWidths((prev) => ({ ...prev, [columnId]: clampedWidth }));
};

/**
 * Resets column widths to their default values
 */
export const resetColumnWidths = (
  fields: Record<string, SimpleFieldConfig>,
  setColumnWidths: (widths: Record<string, number>) => void
): void => {
  const defaultWidths: Record<string, number> = {};
  Object.entries(fields).forEach(([fieldId, fieldConfig]) => {
    defaultWidths[fieldId] = fieldConfig.width || 150;
  });
  setColumnWidths(defaultWidths);
};

/** Minimum width for a column when its cell is being edited (so edit UI doesn't spill into adjacent columns) */
const EDITING_COLUMN_MIN_WIDTH = 320;

/**
 * Gets the column width style for a field.
 * When this column is being edited (editingCell?.fieldId === fieldId), minWidth is increased so the edit UI fits.
 */
export const getColumnStyle = (
  fieldId: string,
  fieldConfig: SimpleFieldConfig | undefined,
  columnWidths: Record<string, number>,
  editingCell?: { rowId: string; fieldId: string } | null
): { width: number; minWidth: number; maxWidth: number; flexShrink: number } => {
  const width = columnWidths[fieldId] || fieldConfig?.width || 180;
  const baseMinWidth = fieldConfig?.minWidth || 120;
  const minWidth =
    editingCell?.fieldId === fieldId
      ? Math.max(baseMinWidth, EDITING_COLUMN_MIN_WIDTH)
      : baseMinWidth;
  const maxWidth = fieldConfig?.maxWidth || 500;

  return {
    width: editingCell?.fieldId === fieldId ? Math.max(width, minWidth) : width,
    minWidth,
    maxWidth,
    flexShrink: 0,
  };
};

// ============================================================================
// DRAG AND DROP FUNCTIONALITY
// ============================================================================

/**
 * Handles drag start for column reordering
 */
export const handleDragStart = (
  e: React.DragEvent,
  columnId: string,
  setDraggedColumn: (columnId: string | null) => void
): void => {
  setDraggedColumn(columnId);
  e.dataTransfer.effectAllowed = "move";
};

/**
 * Handles drag over for column reordering
 */
export const handleDragOver = (e: React.DragEvent): void => {
  e.preventDefault();
  e.dataTransfer.dropEffect = "move";
};

/**
 * Handles drop for column reordering
 */
export const handleDrop = (
  e: React.DragEvent,
  targetColumnId: string,
  draggedColumn: string | null,
  columnOrder: string[],
  setColumnOrder: (order: string[]) => void,
  setDraggedColumn: (columnId: string | null) => void
): void => {
  e.preventDefault();
  if (!draggedColumn || draggedColumn === targetColumnId) return;

  const newOrder = [...columnOrder];
  const draggedIndex = newOrder.indexOf(draggedColumn);
  const targetIndex = newOrder.indexOf(targetColumnId);

  // Remove dragged column and insert at new position
  newOrder.splice(draggedIndex, 1);
  newOrder.splice(targetIndex, 0, draggedColumn);

  setColumnOrder(newOrder);
  setDraggedColumn(null);
};

/**
 * Handles drag end for column reordering
 */
export const handleDragEnd = (setDraggedColumn: (columnId: string | null) => void): void => {
  setDraggedColumn(null);
};
